// Concierge portal shell — shared chrome for Client / Partner / Introducer
// portals so they look and feel like one product (2026-06-25).
//
// Provides:
//   - <PortalShell> wrapper: cream bg + faint spiral watermark + top nav
//     with the real horizontal Solara lockup, portal label, and action slot
//     (entity switcher, sign-out, etc.).
//   - <PortalGreeting> strip with serif heading + smaller subtitle.
//   - <KpiTile> primitive used in the 4-tile KPI strip across all portals.
//
// Brand decisions baked in:
//   - solara-horizontal-white.png as the nav lockup. This is the
//     white-text variant of solara-vertical.png (file naming is inverted;
//     vertical.png is the HORIZONTAL lockup — see
//     reference_solara_logo_files in memory). The original PNG has navy
//     wordmark text which disappeared on the navy nav background; the
//     -white variant has the wordmark recoloured to white while keeping
//     the gold spiral icon intact. Generated 2026-06-25 by PIL pass over
//     solara-vertical.png — see HF-Nav-Logo-White PR.
//   - solara-icon.png as the fixed-position watermark at 2.5% opacity.
//   - #021d49 navy, #bd9a59 gold, #f0ede4 cream page background.
//   - font-serif headlines (Playfair Display), Inter body.
//
// Note: ClientPortal.jsx still inlines its own shell — we intentionally
// did NOT refactor it to use this component because it's just-deployed and
// working. Apply this to new portals first; if a regression in client
// portal ever needs fixing we can swap it in then.

import { Link } from "react-router-dom";

const LOGO_HORIZONTAL = "/logos/solara-horizontal-white.png";  // white-text variant for navy nav
const SPIRAL_ICON = "/logos/solara-icon.png";

export default function PortalShell({
  portalLabel,
  brandHref = "/",
  rightActions = null,
  children,
}) {
  return (
    <div className="min-h-screen bg-[#f0ede4] relative">
      {/* Watermark — large spiral, very faint, fixed centre */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 flex items-center justify-center opacity-[0.025]"
        style={{ zIndex: 0 }}
      >
        <img
          src={SPIRAL_ICON}
          alt=""
          className="w-[720px] h-[720px] max-w-[80vw] max-h-[80vh] object-contain"
        />
      </div>

      {/* Top nav */}
      <header className="bg-[#021d49] text-white relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to={brandHref} className="flex items-center gap-3 min-w-0" data-testid="portal-brand">
            <img
              src={LOGO_HORIZONTAL}
              alt="Solara Global Capital Partners"
              className="h-9 w-auto flex-shrink-0"
            />
            {portalLabel && (
              <span className="hidden md:inline-block border-l border-white/20 pl-3 ml-1 text-[11px] uppercase tracking-wider text-[#bd9a59] leading-none">
                {portalLabel}
              </span>
            )}
          </Link>
          <div className="flex items-center gap-2">
            {rightActions}
          </div>
        </div>
      </header>

      {/* Body — children render inside the watermark-aware z-index stack */}
      <main className="relative z-10">
        {children}
      </main>
    </div>
  );
}

// ---------- shared sub-components ----------

export function PortalGreeting({ title, subtitle }) {
  return (
    <section className="max-w-[1400px] mx-auto px-6 pt-7 pb-2">
      <h1 className="font-serif text-2xl text-[#021d49]" data-testid="portal-greeting">
        {title}
      </h1>
      {subtitle && (
        <p className="text-[13px] text-gray-600 mt-1">{subtitle}</p>
      )}
    </section>
  );
}

export function KpiStrip({ children }) {
  return (
    <section className="max-w-[1400px] mx-auto px-6 pt-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {children}
      </div>
    </section>
  );
}

export function KpiTile({ label, value, accent = false, sub = null }) {
  return (
    <div className="bg-white border border-[#bd9a59]/30 rounded-lg px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div
        className={`font-serif text-2xl mt-1 leading-none ${accent ? "text-[#bd9a59]" : "text-[#021d49]"}`}
      >
        {value}
      </div>
      {sub && (
        <div className="text-[11px] text-gray-400 mt-1">{sub}</div>
      )}
    </div>
  );
}

export function PortalBody({ children, className = "" }) {
  return (
    <section className={`max-w-[1400px] mx-auto px-6 pt-4 pb-10 ${className}`}>
      {children}
    </section>
  );
}

// Generic "Solara contact" card — borrows from the client portal's
// MessagingPanel header but without messaging. Used as a sidebar element
// on portals that don't have per-deal threads (Partner, Introducer).
export function SolaraContactCard({
  title = "Your Solara contact",
  name = "Solara Loans Team",
  role = "Your Solara contact",
  email = "loans@sgcap.com.au",
  phone = "",
}) {
  return (
    <div className="bg-white border border-[#bd9a59]/30 rounded-xl overflow-hidden">
      <div className="p-4">
        <div className="text-[12px] uppercase tracking-wider text-[#021d49] font-medium mb-3">
          {title}
        </div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#021d49] flex items-center justify-center shrink-0 overflow-hidden">
            <img
              src={SPIRAL_ICON}
              alt=""
              className="w-7 h-7 object-contain"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#021d49] truncate">{name}</div>
            <div className="text-[11px] text-gray-500 truncate">{role}</div>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          {phone && (
            <a
              href={`tel:${phone}`}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs rounded-md bg-[#021d49] text-white hover:bg-[#052b66]"
            >
              Call
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs rounded-md bg-[#bd9a59] text-[#021d49] hover:bg-[#a8854a]"
            >
              Email
            </a>
          )}
        </div>
        <div className="mt-3 text-[11px] text-gray-500 italic">
          Replies within 1 business hour
        </div>
      </div>
    </div>
  );
}
